/**
 * Calculate statistics from survey responses
 * @param {Array} responseData - The survey response data
 * @param {Object} surveyData - The survey data
 * @returns {Object} The calculated statistics
 */
export const calculateStats = (responseData, surveyData) => {
    const totalQuestions = surveyData?.questions?.length || 0;
    const submittedResponses = responseData.filter(r => r.isSubmitted);
    const incompleteResponses = responseData.filter(r => !r.isSubmitted);

    const stats = {
        totalResponses: responseData.length,
        submittedResponses: submittedResponses.length,
        incompleteResponses: incompleteResponses.length,
        questionsWithFollowups: {},
        averageFollowupsPerQuestion: 0,
        responsesByDate: [],
        completionRate: 0,
        averageQuestionsAnswered: 0,
        abandonmentPoints: {},
    };

    // Calculate completion rate
    stats.completionRate = (stats.submittedResponses / stats.totalResponses * 100).toFixed(1);

    // Calculate follow-up stats
    let totalFollowups = 0;
    let questionCount = 0;

    // Create a map for responses by date and track submission status
    const responsesByDate = responseData.reduce((acc, response) => {
        const date = new Date(response.createdAt).toISOString().split('T')[0];
        if (!acc[date]) {
            acc[date] = { total: 0, submitted: 0, incomplete: 0 };
        }
        acc[date].total += 1;
        if (response.isSubmitted) {
            acc[date].submitted += 1;
        } else {
            acc[date].incomplete += 1;
        }
        return acc;
    }, {});

    // Convert to array and sort by date
    const sortedDates = Object.keys(responsesByDate).sort();
    let cumulativeTotal = 0;
    let cumulativeSubmitted = 0;
    let cumulativeIncomplete = 0;

    stats.responsesByDate = sortedDates.map(date => {
        cumulativeTotal += responsesByDate[date].total;
        cumulativeSubmitted += responsesByDate[date].submitted;
        cumulativeIncomplete += responsesByDate[date].incomplete;

        return {
            date,
            total: responsesByDate[date].total,
            submitted: responsesByDate[date].submitted,
            incomplete: responsesByDate[date].incomplete,
            cumulativeTotal,
            cumulativeSubmitted,
            cumulativeIncomplete
        };
    });

    // Calculate follow-up statistics
    responseData.forEach(response => {
        response.answers.forEach(answer => {
            if (!stats.questionsWithFollowups[answer.questionId]) {
                stats.questionsWithFollowups[answer.questionId] = {
                    followupCount: 0,
                    totalFollowups: 0
                };
                questionCount++;
            }

            if (answer.followups?.length > 0) {
                stats.questionsWithFollowups[answer.questionId].followupCount++;
                stats.questionsWithFollowups[answer.questionId].totalFollowups += answer.followups.length;
                totalFollowups += answer.followups.length;
            }
        });
    });

    stats.averageFollowupsPerQuestion = questionCount > 0 ?
        (totalFollowups / questionCount).toFixed(2) : 0;

    // Calculate average questions answered
    const totalQuestionsAnswered = responseData.reduce((sum, response) => {
        return sum + response.answers.length;
    }, 0);

    stats.averageQuestionsAnswered = (totalQuestionsAnswered / stats.totalResponses).toFixed(1);

    // Initialize abandonment points
    const abandonmentPoints = {};

    // Initialize counters for each question
    if (surveyData && surveyData.questions) {
        surveyData.questions.forEach((question, index) => {
            abandonmentPoints[index] = 0;
        });
    }

    // Count incomplete responses to find out where users actually stop
    incompleteResponses.forEach(response => {
        // Skip if no answers array
        if (!response.answers || response.answers.length === 0) {
            abandonmentPoints[0] = (abandonmentPoints[0] || 0) + 1;
            return;
        }

        // Create a mapping of question IDs to their index for easier lookup
        const questionIndexMap = {};
        if (surveyData && surveyData.questions) {
            surveyData.questions.forEach((question, index) => {
                questionIndexMap[question._id.toString()] = index;
            });
        }

        // Find the first question with an empty or skipped answer
        let abandonmentIndex = null;

        // First sort the answers by their question index to ensure we process in order
        const sortedAnswers = [...response.answers].sort((a, b) => {
            const indexA = questionIndexMap[a.questionId.toString()] || 0;
            const indexB = questionIndexMap[b.questionId.toString()] || 0;
            return indexA - indexB;
        });

        for (let i = 0; i < sortedAnswers.length; i++) {
            const answer = sortedAnswers[i];
            const questionId = answer.questionId.toString();
            const questionIndex = questionIndexMap[questionId];

            if (questionIndex === undefined) continue;

            // Check if this answer is empty or skipped
            const isEmpty = !answer.answerText ||
                answer.answerText.trim() === '' ||
                answer.answerText === '[SKIPPED]';

            if (isEmpty) {
                abandonmentIndex = questionIndex;
                break;
            }
        }

        // If no empty answer was found but the survey is incomplete,
        // assume abandonment at the next question after the last answered one
        if (abandonmentIndex === null) {
            // Find the highest question index that was answered
            let highestIndex = -1;
            response.answers.forEach(answer => {
                const questionId = answer.questionId.toString();
                const questionIndex = questionIndexMap[questionId];

                if (questionIndex !== undefined && questionIndex > highestIndex) {
                    highestIndex = questionIndex;
                }
            });

            // Abandonment happened at the next question, or at the last question if there is no next
            abandonmentIndex = Math.min(highestIndex + 1, surveyData.questions.length - 1);
        }

        // Increment the abandonment counter for this index
        abandonmentPoints[abandonmentIndex] = (abandonmentPoints[abandonmentIndex] || 0) + 1;
    });

    stats.abandonmentPoints = abandonmentPoints;

    return stats;
};